<template>
  <div>
    <PageHeader :items="items" :optionalItems="optionalItems" />

    <div class="dextop-table-view">
      <div class="table-responsive api-keys-table">
        <vue-good-table
          styleClass="vgt-table striped"
          :columns="columns"
          :fixed-header="false"
          :rows="rows"
          :search-options="{
            enabled: true,
            externalQuery: searchTerm,
            trigger: 'enter',
          }"
          :pagination-options="{
            enabled: false,
            perPage: pageLength,
          }"
          mode="remote"
          @on-page-change="onPageChange"
          @on-sort-change="onSortChange"
          @on-per-page-change="onPerPageChange"
        >
          <div slot="emptystate" style="text-align: center">
            {{ $t("noDataForTable") }}
          </div>
          <template slot="table-row" slot-scope="props">
            <span
              class="d-flex align-items-center justify-content-center gap-2"
              v-if="props.column.field === 'action'"
            >
              <div
                class="cursor-pointer"
                :title="$t('managePrices')"
                @click="openPricesModal(props.row)"
              >
                <feather-icon size="16" icon="DollarSignIcon" />
              </div>
              <div
                class="cursor-pointer"
                :title="props.row.isActive ? $t('inactive') : $t('activate')"
                @click="toggleActive(props.row)"
              >
                <feather-icon
                  size="16"
                  :icon="props.row.isActive ? 'XCircleIcon' : 'CheckCircleIcon'"
                />
              </div>
              <div
                v-if="$can(`${$route.meta.permission}.edit`)"
                class="cursor-pointer"
                :title="$t('edit')"
                @click="$router.push(`/plans/${props.row.id}/edit`)"
              >
                <feather-icon size="16" icon="Edit2Icon" />
              </div>
              <div
                v-if="$can('backoffice-plan.delete')"
                class="cursor-pointer"
                :title="$t('delete')"
                @click="destroy(props.row.id)"
              >
                <feather-icon size="16" icon="TrashIcon" />
              </div>
            </span>

            <span v-else-if="props.column.field === 'isActive'">
              {{ props.row.isActive ? $t("yes") : $t("no") }}
            </span>

            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
      <div class="d-flex align-items-center justify-content-between">
        <b-form-group>
          <div class="d-flex align-items-center">
            <span class="text-nowrap"> {{ $t("rowsPerPage") }} </span>
            <b-form-select
              v-model="pageLength"
              :options="['25', '50', '100']"
              class="ml-1"
              @input="(value) => onPerPageChange({ pageLength: value })"
            />
          </div>
        </b-form-group>
        <div>
          <b-pagination
            :value="1"
            :total-rows="totalRecords"
            :per-page="pageLength"
            first-number
            last-number
            align="right"
            prev-class="prev-item"
            next-class="next-item"
            class="mt-1 mb-0"
            @input="(value) => onPageChange({ currentPage: value })"
          >
            <template #prev-text>
              <feather-icon icon="ChevronLeftIcon" size="18" />
            </template>
            <template #next-text>
              <feather-icon icon="ChevronRightIcon" size="18" />
            </template>
          </b-pagination>
        </div>
      </div>
    </div>

    <div class="reponsive-view-cards">
      <b-row>
        <b-col cols="12" lg="4" md="6" v-for="row in rows" :key="row.id">
          <div class="card">
            <div
              class="card-header d-flex align-items-center justify-content-between"
            >
              <h5>{{ row.name }}</h5>
              <div class="d-flex gap-3">
                <div
                  class="cursor-pointer"
                  :title="$t('managePrices')"
                  @click="openPricesModal(row)"
                >
                  <feather-icon size="18" icon="DollarSignIcon" />
                </div>
                <div
                  v-if="$can(`${$route.meta.permission}.edit`)"
                  class="cursor-pointer"
                  :title="$t('edit')"
                  @click="$router.push(`/plans/${row.id}/edit`)"
                >
                  <feather-icon size="18" icon="Edit2Icon" />
                </div>
                <div
                  class="cursor-pointer"
                  :title="row.isActive ? $t('inactive') : $t('activate')"
                  @click="toggleActive(row)"
                >
                  <feather-icon
                    size="18"
                    :icon="row.isActive ? 'XCircleIcon' : 'CheckCircleIcon'"
                  />
                </div>
                <div
                  v-if="$can('backoffice-plan.delete')"
                  class="cursor-pointer"
                  @click="destroy(row.id)"
                  :title="$t('delete')"
                >
                  <feather-icon size="18" icon="TrashIcon" />
                </div>
              </div>
            </div>

            <div class="card-body pt-0">
              <ul class="card-list">
                <li>
                  <div class="left">
                    <strong>{{ $t("price") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.price }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("units") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.units }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("dentawaxAmount") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.dentawaxAmount }}</span>
                  </div>
                </li>
                <li>
                  <div class="left">
                    <strong>{{ $t("isActive") }}: </strong>
                  </div>
                  <div class="right">
                    <span>{{ row.isActive ? $t("yes") : $t("no") }}</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </b-col>
        <b-col cols="12">
          <div class="d-flex justify-content-center mt-2">
            <b-pagination
              :value="1"
              :total-rows="totalRecords"
              :per-page="pageLength"
              first-number
              last-number
              align="right"
              prev-class="prev-item"
              next-class="next-item"
              class="mt-1 mb-0"
              @input="(value) => onPageChange({ currentPage: value })"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </div>
        </b-col>
      </b-row>
    </div>

    <PlanPricesModal
      v-model="showPricesModal"
      :plan="selectedPlan"
      @updated="onPricesUpdated"
    />
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import PlanPricesModal from "@/views/plans/components/PlanPricesModal.vue";

export default {
  components: {
    PageHeader,
    PlanPricesModal,
  },
  computed: {
    optionalItems() {
      return {
        createBtn1: {
          show: true,
          text: this.$t("createPlan"),
          icon: "PlusIcon",
          path: "/plans/create",
        },
      };
    },
    items() {
      return [
        {
          text: "Dental Twin",
          to: "/home",
        },
        {
          text: this.$t("plans"),
          active: true,
        },
      ];
    },
    columns() {
      return [
        {
          label: this.$t("name"),
          field: "name",
        },
        {
          label: this.$t("price"),
          field: "price",
        },
        {
          label: this.$t("units"),
          field: "units",
        },
        {
          label: this.$t("pricePerUnit"),
          field: "pricePerUnit",
        },
        {
          label: this.$t("dentawaxAmount"),
          field: "dentawaxAmount",
        },
        {
          label: this.$t("isActive"),
          field: "isActive",
        },
        {
          tdClass: "vgt-center-align",
          thClass: "vgt-center-align",
          label: this.$t("action"),
          field: "action",
          sortable: false,
        },
      ];
    },
  },
  data() {
    return {
      totalRecords: 0,
      pageLength: 25,
      page: 1,
      form: {
        sortBy: "name",
        sortOrder: "asc",
      },
      rows: [],
      searchTerm: "",
      showPricesModal: false,
      selectedPlan: null,
    };
  },
  async created() {
    this.loadItems();
  },
  methods: {
    updateParams(newProps) {
      this.serverParams = Object.assign({}, this, newProps);
    },
    onPageChange(params) {
      this.page = params.currentPage;
      this.loadItems();
    },
    onPerPageChange(params) {
      this.updateParams({ pageLength: params.pageLength });
      this.loadItems();
    },
    onSortChange(params) {
      this.form.sortOrder = params[0].type;
      if (params[0].type == "none") this.form.sortOrder = "asc";
      this.form.sortBy = params[0].field;
      this.loadItems();
    },
    async loadItems() {
      try {
        this.$store.commit("showLoader", true);
        let response = await this.$store.dispatch("plans/list", {
          page: this.page,
          ...this.form,
          perPage: this.pageLength,
        });
        this.rows = response?.data?.data;
        this.totalRecords = response?.data?.total;
      } catch (e) {
        console.error("api error ___", e);
      } finally {
        this.$store.commit("showLoader", false);
      }
    },
    openPricesModal(row) {
      this.selectedPlan = row;
      this.showPricesModal = true;
    },
    async onPricesUpdated() {
      await this.loadItems();
      if (this.selectedPlan) {
        this.selectedPlan =
          this.rows.find((row) => row.id === this.selectedPlan.id) || null;
      }
    },
    async destroy(id) {
      const confirmed = await this.showConfirmationDialog({
        title: this.$t("doYouWantToDeleteThisRecord"),
        subText: this.$t("yesDeleteIt"),
      });

      if (confirmed) {
        await this.$store.dispatch("plans/destroy", id).finally(() => {
          this.loadItems();
        });
      }
    },
    async toggleActive(row) {
      const title = row.isActive
        ? this.$t("doYouWantToInactiveThisRecord")
        : this.$t("doYouWantToActiveThisRecord");

      const confirmed = await this.showConfirmationDialog({
        title,
        subText: this.$t("yesUpdateIt"),
      });

      if (confirmed) {
        try {
          this.$store.commit("showLoader", true);
          await this.$store.dispatch("plans/toggleActive", row.id);
          this.loadItems();
        } catch (e) {
          console.error("api error ___", e);
        } finally {
          this.$store.commit("showLoader", false);
        }
      }
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
